import type { SerializedNode } from "@craftjs/core";
import type { useFlags } from "launchdarkly-react-client-sdk";
import type { StaticImageData } from "next/image";

import type { ThemeTemplateOptions } from "@/components/editor/themes/types";
import type { PageTheme } from "@/components/page-provider/page-provider-component";
export type { RootState } from "@/redux/store";

export * from "@/components/editor/themes/types";
export * from "./error";

export type BlockCategory =
  | "header"
  | "schedule"
  | "sections"
  | "speakers"
  | "sponsors"
  | "expo"
  | "media";

export enum BlockVariant {
  MANUAL = "manual",
  SYNCED = "synced",
  SYNCED_EXT = "synced_extended",
}

export interface BlockSelection {
  Component: (options: ThemeTemplateOptions) => React.ReactElement;
  deprecated?: boolean;
  displayName: string;
  previewImage: StaticImageData;
  stringMap?: (t: TextCustomizations) => Record<string, string>;
  variant?: BlockVariant;
  /**
   * [optional] Callback to assert if the block can be selected from left sidebar (_block-selection-component.tsx_).
   * Useful when block(s) is behind a feature flag.
   */
  selectable?: ({
    flags,
    eventSeries,
  }: {
    flags: ReturnType<typeof useFlags>;
    eventSeries: EventSeries[];
  }) => boolean;
  previewImageContainerStyle?: React.CSSProperties;
}

export type Viewport = "mobile" | "tablet" | "desktop";

export type Font = {
  name?: string;
  value: string;
  provider: string;
};

export type Color = string;

export type RailsId = string | number;

export type User = {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  external_id: string;
};

export type Speaker = {
  id: RailsId;
  /**
   * @deprecated field will be removed in speakers V2 endpoint
   * use `first_name` and `last_name` instead.
   */
  name: string;
  first_name: string;
  last_name: string;
  headline: string;
  image_url: string;
  social?: {
    linkedin_url?: string;
    twitter_url?: string;
    website_url?: string;
  };
  bio: string;
};

export type SpeakerV3 = Omit<Speaker, "name">;

export type AboutDetail = {
  paragraphHeading: string;
  paragraph: string;
};

export type EventStatus = "draft" | "live" | "deleted" | "preview";
export type EventType = "public_event" | "private_event" | "hidden_event";

export type VenueType = "hybrid" | "virtual" | "onsite";

export type SchedulesPublicEventResponse = {
  event: {
    id: number;
    external_id: string;
    venue_type: VenueType;
    event_kind: string;
    is_lite: boolean;
    name: string;
    timezone: string;
    time_start: string;
    time_end: string;
    organiser_id: number;
    organiser_external_id: string;
    status: string;
    schedule_draft: boolean;
  };
};

export type EventAuthDetails = {
  requireSsoLogin: boolean | null;
  customDomainEvent: boolean | null;
};

export type Event = {
  name: string;
  status: EventStatus;
  slug: string;
  standard_timezone: string;
  time_start: string;
  time_end: string;
  pre_event_time_start: string | null;
  /**
   * @remarks
   * The registration count can be null when connections_enable_people_page
   * feature flag is turned ON
   */
  registration_count: number | null;
  timezone: string;
  description: string;
  external_id: string;
  space_external_id: string | null;
  short_description: string;
  /**
   * @deprecated field will be removed in PAGE-501
   * use `organization` instead.
   */
  organization_id: number;
  event_type: EventType;
  venue_type?: VenueType;
  organization: Organization;
  marketing: Marketing;
  /** Does the event require Enterprise SSO auth */
  require_sso_login: boolean;
  custom_domain_event: boolean;
  favicon_url: string | null;
  created_at: string;
  tracking_integrations?: TrackingIntegrations;
};

type Marketing = {
  analytics_code: string;
  image_url: string;
};

type TrackingIntegrations = {
  data?: TrackingIntegration[];
};
type TrackingIntegration = {
  id: string;
  attributes: { code: string };
  type: string;
};

export type EventWithCustomPages = Event & {
  custom_pages: Page[];
};

export type EventWithCustomDomain = {
  domain: string;
  domainable_type: string;
  slug: string;
};

export enum EventPaymentPlan {
  FREE = "Free",
  PRO = "Pro",
  BUSINESS = "Business",
  BUSINESS_3_months = "Business_three_months",
  BUSINESS_6_months = "Business_six_months",
  BUSINESS_12_months = "Business_twelve_months",
  ENTERPRISE = "Enterprise",
  UNLIMITED = "Unlimited",
  ESSENTIALS_100 = "Essentials_100",
  ESSENTIALS_500 = "Essentials_500",
  ESSENTIALS_1000 = "Essentials_1000",
  ESSENTIALS_2000 = "Essentials_2000",
  ESSENTIALS_3000 = "Essentials_3000",
  ESSENTIALS_5000 = "Essentials_5000",
  ESSENTIALS_7500 = "Essentials_7500",
  ESSENTIALS_10000 = "Essentials_10000",
  ESSENTIALS_20000 = "Essentials_20000",
  EVENTS_ECOMMERCE_100 = "Events_Ecommerce_100",
  EVENTS_ECOMMERCE_500 = "Events_Ecommerce_500",
  EVENTS_ECOMMERCE_1000 = "Events_Ecommerce_1000",
  SALES_LED_PRO = "Sales_led_pro",
  SALES_LED_PRO_PLUS = "Sales_led_pro_plus",
  SALES_LED_PRO_ENTERPRISE = "Sales_led_pro_enterprise",
}

export type Organization = {
  external_id: string;
  name: string;
  about: string | null;
  payment_plan_name: EventPaymentPlan;
  payment_plan_advanced: boolean;
  picture_url: string;
};

export type Sponsor = {
  id: number;
  name: string;
  website: string;
  image_url: string;
};

export type SponsorV3 = Sponsor & {
  tiers: string[];
  priority: number;
};

export type Tier = {
  id: number;
  customTextKey: "gold" | "silver" | "bronze";
  name: string;
  sponsors: number[]; // ordered
};

export type Vendor = {
  id: number;
  booth_size: "mini" | "small" | "medium" | "large";
  name: string;
  headline: string;
  image_url: string;
  background_image: string;
};

export interface CustomFontFace {
  id: string;
  style: string; // TODO: specify style;
  src: string;
}

export interface CustomFont {
  id: string;
  name: string;
  font_faces: CustomFontFace[];
}
export interface FontImport {
  id: number;
  font: CustomFont;
}

export type Page = {
  id: number;
  external_id: string;
  enabled: boolean;
  eventId: number;
  content: Record<string, SerializedNode>;
  properties: {
    fonts: Font[];
    colors: Color[];
    theme: PageTheme;
  };
  entity: Event;
  font_imports: FontImport[];
  revision: number;
  last_updated_by: {
    first_name: string;
    last_name: string;
  };
  updated_at: string;
};

export type Schedule = {
  date: string;
  schedules: ScheduleItem[];
};

export type ScheduleItem = {
  id: number;
  name: string;
  time_end: string;
  time_start: string;
  description: string;
  external_id: string;
  event_part: {
    event_part_type: string;
    event_type_custom_text?: string;
  };
  speakers: Array<Speaker>;
  tags: Array<Tag>;
};

export type AuthDataContextType = {
  token: string;
  user: User;
};

export type TokenResponse = { token: string };

export type CustomDomainSsoTokenResponse = TokenResponse;

export type SsoValidationResponse = TokenResponse;

export type EventAuthEnabledResponse = { auth_enabled: boolean };

export interface TextCustomizations {
  custom_text: Record<string, Record<string, string>> | null;
  default_language: string;
}

export enum Actions {
  None = "None",
  TicketSelector = "TicketSelector",
  SectionOfPage = "SectionOfPage",
  ExternalLink = "ExternalLink",
  TopOfPage = "TopOfPage",
  BottomOfPage = "BottomOfPage",
  AddToCalendar = "AddToCalendar",
}

export type AddToCalendarPayload = {
  externalId: ScheduleItem["external_id"];
  name: ScheduleItem["name"];
  timeStart: ScheduleItem["time_start"];
  timeEnd: ScheduleItem["time_end"];
};

export type Action =
  | {
      kind: Actions.None;
    }
  | {
      kind: Actions.TicketSelector;
    }
  | {
      kind: Actions.SectionOfPage;
      nodeId: string;
    }
  | {
      kind: Actions.ExternalLink;
      href: string;
    }
  | {
      kind: Actions.TopOfPage;
    }
  | {
      kind: Actions.BottomOfPage;
    }
  | {
      kind: Actions.AddToCalendar;
      payload: AddToCalendarPayload;
    };

export type SponsorLayout =
  | "golden"
  | "silver"
  | "bronze"
  | "tier 4"
  | "tier 5"
  | "tier 6"
  | "tier 7"
  | "tier 8"
  | "tier 9"
  | "tier 10"
  | "no_tier";

export type SponsorTierForLocale =
  | "gold"
  | "silver"
  | "bronze"
  | "tier-4"
  | "tier-5"
  | "tier-6"
  | "tier-7"
  | "tier-8"
  | "tier-9"
  | "tier-10"
  | "no_tier";

export type TextAlign = "left" | "center" | "right";

export type Tag = {
  external_id: string | null;
  name: string;
  priority: number;
};

export enum RECORDING_CATEGORY {
  MAIN = "main",
  BACKUP = "backup",
  REHEARSAL = "rehearsal",
  CUT = "cut",
  UPLOAD = `upload`,
  DEFAULT = "default",
}

export type RecordingCategoryUnion = `${RECORDING_CATEGORY}`;

export enum VIDEO_EDIT_STATUSES {
  success = "success",
  failure = "failure",
  processing = "processing",
  trimmingRequested = "trimming_requested",
}

export type VideoEditStatusUnion = `${VIDEO_EDIT_STATUSES}`;

/**
 * A frontend representation of a single Recording (in camelCase)
 */
export type Recording = {
  playableUrl: string | null;
  id: string;
  title: string;
  description: string;
  category: RecordingCategoryUnion;
  downloadUrl: string | null;
  failure_reason: string | null;
  thumbnail: string | null;
  startedAt: string;
  endedAt?: string;
  duration: number;
  eventPart: {
    id: string;
    name?: string;
    type: string;
  };
  published: boolean;
  parentRecordingId: string | null;
  status: VideoEditStatusUnion;
  statusChangedAt: string | null;
  simulives: Simulive[];
  isOriginal: boolean;
  createdAt: string;
  aspectRatio?: string | null;
};

export type Simulive = {
  id: number;
  uuid: string;
  schedule_id: number;
  video_channel_id: number;
};

/**
 * A backend representation of a single Recording (in snake_case)
 */
export type RecordingRaw = {
  id: string;
  title: string;
  description: string;
  category: RecordingCategoryUnion;
  duration: number;
  failure_reason: string | null;
  started_at: string;
  thumbnail: string | null;
  ended_at?: string;
  playable_url: string | null;
  download_url: string | null;
  event_part_name: string;
  event_part_type: string;
  event_part_id: string;
  published: boolean;
  parent_recording_id: string | null;
  status: VideoEditStatusUnion;
  status_changed_at: string | null;
  simulives: Simulive[];
  is_original: boolean;
  created_at: string;
  aspect_ratio?: string | null;
};

export type RecordingListRawResponse = {
  data: RecordingRaw[];
};

export type EventSeries = {
  name: string;
  external_id: string;
};

export type EventDetails = {
  name: string;
  external_id: string;
  event_url: string;
  description: string;
  time_start: string;
  time_end: string;
  medium_picture: string;
  large_picture: string;
  original_picture: string;
};

export type RelatedEvents = {
  eventSeries: EventSeries[];
  eventSeriesEvents: { [seriesExternalId: string]: EventDetails[] };
  eventSeriesLoadingStatus: string;
  eventSeriesEventsLoadingStatus: { [seriesExternalId: string]: string };
};
